import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { ActionRow } from '@/components/patterns/action-row'
import CreateActionButton from '@/components/patterns/create-action-button'
import { EmptyState } from '@/components/patterns/empty-state'
import SaveBar from '@/components/forms/SaveBar'
import StorefrontPreviewFrame from '@/components/forms/DesignForm/StorefrontPreviewFrame'
import { buildDemoStorefrontUrl } from '@/components/forms/DesignForm/storefrontPreviewConfig'
import {
  SeasonalReadinessBadge,
  SeasonalReadinessSummary,
} from '@/components/seasonal/SeasonalReadinessNotice'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import { useAppForm } from '@/lib/form'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import {
  collectionQueries,
  designCollectionsFilters,
} from '@/lib/services/collections/collections.query'
import {
  designQueries,
  useActivateDesignMutation,
  useDesigns,
  useUpdateDesignScheduleStatesMutation,
} from '@/lib/services/designs/designs.query'
import { getDesignScheduleSummary } from '@/lib/services/designs/designs.scheduleSummary'
import { getDesignOriginLabel } from '@/lib/services/designs/designs.schema'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { designProductsFilters, productQueries } from '@/lib/services/products/products.query'
import { storeQueries } from '@/lib/services/stores/stores.query'
import { getErrorMessage } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import { useMediaQuery } from '@uidotdev/usehooks'
import {
  Calendar,
  CalendarCheck,
  CalendarOff,
  CalendarX,
  Eye,
  ExternalLink,
  LoaderCircle,
  Palette,
  Pencil,
  PlayCircle,
  Radio,
  Wallpaper,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

const safeDateValue = (value) => {
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.valueOf() : 0
}

const classNames = (...values) => values.filter(Boolean).join(' ')

function DesignsSplitLayout({ previewPanel, previewPanelRef, settingsPanel }) {
  const isWideScreen = useMediaQuery('(min-width: 1280px)')

  if (!isWideScreen) {
    return (
      <div className="space-y-4">
        <div>{settingsPanel}</div>
        <div
          ref={previewPanelRef}
          className="h-[min(48rem,calc(100dvh-7rem))] min-h-[34rem] scroll-mt-4 pb-4"
        >
          {previewPanel}
        </div>
      </div>
    )
  }

  return (
    <ResizablePanelGroup
      direction="horizontal"
      autoSaveId="seasonal-website-updates-panels"
      className="h-full min-h-0"
    >
      <ResizablePanel defaultSize={38} minSize={28} maxSize={52} className="min-w-[24rem]">
        <div className="h-full min-h-0 pr-2">{settingsPanel}</div>
      </ResizablePanel>

      <ResizableHandle
        withHandle
        className="mx-1 w-2 rounded-full bg-transparent transition-colors hover:bg-primary/15"
      />

      <ResizablePanel defaultSize={62} minSize={44}>
        <div className="h-full min-h-0 pl-2">{previewPanel}</div>
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}

function designStartsAtValue(design) {
  const startsAt =
    design?.schedule?.next_starts_at ?? design?.schedule?.starts_on ?? design?.schedule?.starts_at

  return startsAt ? safeDateValue(startsAt) || Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER
}

export function buildDesignScheduleFormValues(designs = []) {
  return {
    schedules: Object.fromEntries(
      designs.map((design) => [
        designScheduleFormKey(design?.id),
        {
          id: design?.id,
          has_schedule: Boolean(design?.schedule),
          is_enabled: Boolean(design?.schedule?.is_enabled),
        },
      ])
    ),
  }
}

export function designScheduleFormKey(designId) {
  return `design_${designId}`
}

const designScheduleEntries = (schedules = {}) =>
  Array.isArray(schedules) ? schedules : Object.values(schedules)

export function buildChangedDesignSchedulePayloads(valueSchedules = {}, baselineSchedules = {}) {
  const baselineById = new Map(
    designScheduleEntries(baselineSchedules).map((schedule) => [String(schedule.id), schedule])
  )

  return designScheduleEntries(valueSchedules)
    .map((schedule) => {
      const baseline = baselineById.get(String(schedule.id))

      if (!baseline?.has_schedule) {
        return null
      }

      const isEnabled = Boolean(schedule.is_enabled)

      if (isEnabled === Boolean(baseline.is_enabled)) {
        return null
      }

      return {
        designId: baseline.id,
        is_enabled: isEnabled,
      }
    })
    .filter(Boolean)
}

function designScheduleState({
  activeDesignId,
  activeScheduledDesignId,
  design,
  scheduleEnabled = null,
}) {
  const isActive = activeDesignId && Number(design?.id) === Number(activeDesignId)
  const isScheduledActive =
    isActive && activeScheduledDesignId && Number(design?.id) === Number(activeScheduledDesignId)
  const hasSchedule = Boolean(design?.schedule)
  const isScheduleEnabled = hasSchedule
    ? Boolean(scheduleEnabled ?? design?.schedule?.is_enabled)
    : false

  if (isScheduledActive && isScheduleEnabled) {
    return {
      badge: 'Live now',
      badgeVariant: 'success',
      icon: Radio,
      isActive,
      isScheduledActive,
      hasSchedule,
      isScheduleEnabled,
      label: 'Live now',
    }
  }

  if (isActive && !isScheduledActive) {
    return {
      badge: 'Live manually',
      badgeVariant: 'success',
      icon: PlayCircle,
      isActive,
      isScheduledActive,
      hasSchedule,
      isScheduleEnabled,
      label: 'Live manually',
    }
  }

  if (isScheduleEnabled) {
    return {
      badge: 'Schedule on',
      badgeVariant: 'brand',
      icon: CalendarCheck,
      isActive,
      isScheduledActive,
      hasSchedule,
      isScheduleEnabled,
      label: 'Schedule on',
    }
  }

  if (hasSchedule) {
    return {
      badge: 'Schedule off',
      badgeVariant: 'secondary',
      icon: CalendarOff,
      isActive,
      isScheduledActive,
      hasSchedule,
      isScheduleEnabled,
      label: 'Schedule off',
    }
  }

  return {
    badge: 'No schedule',
    badgeVariant: 'outline',
    icon: CalendarX,
    isActive,
    isScheduledActive,
    hasSchedule,
    isScheduleEnabled,
    label: 'No schedule',
  }
}

function EverydayWebsiteCard({ design, isActive, isReadOnly, orgId }) {
  return (
    <div className="rounded-xl border border-border/70 bg-card p-3.5">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-interaction">
          <Palette className="size-4.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="cardTitle" className="pb-0">
              Everyday design
            </Typography>
            {isActive ? <Badge variant="success">Live now</Badge> : null}
          </div>
          <Typography variant="supporting" className="mt-1 block">
            Customers see this whenever no seasonal update is running.
          </Typography>
          {design?.id && !isReadOnly ? (
            <Button asChild size="inline" variant="link" className="mt-1.5 justify-start">
              <Link
                to="/$orgId/website/builder"
                params={{ orgId: String(orgId) }}
                search={{ section: 'brand-layout' }}
              >
                <Pencil className="size-3.5" />
                Edit everyday design
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function SeasonalUpdateRow({
  activeDesignId,
  activeScheduledDesignId,
  defaultDesign,
  design,
  form,
  isPending,
  isReadOnly,
  scheduleKey,
  scheduleEnabled,
  isSelected,
  onReturnToDefault,
  onPreview,
  onSelect,
}) {
  const path = usePathHelper()
  const scheduleState = designScheduleState({
    activeDesignId,
    activeScheduledDesignId,
    design,
    scheduleEnabled,
  })
  const scheduleSummary = getDesignScheduleSummary({
    design,
    isActive: scheduleState.isActive,
  })
  const StatusIcon = scheduleState.icon
  const canToggleSchedule = !isReadOnly && scheduleState.hasSchedule && !isPending
  const canReturnToDefault =
    !isReadOnly &&
    scheduleState.isActive &&
    !scheduleState.isScheduledActive &&
    defaultDesign?.id &&
    !isPending
  const scheduleToggleLabel = scheduleState.hasSchedule
    ? scheduleState.isScheduleEnabled
      ? 'Schedule on'
      : 'Schedule off'
    : 'No schedule'
  const scheduleToggleAriaLabel = scheduleState.hasSchedule
    ? `${scheduleState.isScheduleEnabled ? 'Turn schedule off for' : 'Turn schedule on for'} ${design?.name || 'seasonal update'}`
    : `No schedule for ${design?.name || 'seasonal update'}`

  return (
    <Card className="@container/seasonal-update-row overflow-hidden" selected={isSelected}>
      <CardContent className="grid gap-4 p-4 @[47rem]/seasonal-update-row:grid-cols-[minmax(0,1fr)_auto] @[47rem]/seasonal-update-row:items-start">
        <ActionRow
          padding="none"
          radius="surface"
          className="min-w-0 flex-col items-stretch"
          onClick={() => onSelect(design.id)}
        >
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={scheduleState.badgeVariant}>
                <StatusIcon className="size-3.5" />
                {scheduleState.badge}
              </Badge>
              <SeasonalReadinessBadge design={design} isEnabled={scheduleState.isScheduleEnabled} />
              <Badge variant={design?.design_origin === 'platform' ? 'brand' : 'outline'}>
                {getDesignOriginLabel(design)}
              </Badge>
            </div>

            <div className="min-w-0">
              <CardTitle className="line-clamp-2 text-lg">
                {design?.name || 'Untitled Update'}
              </CardTitle>
              <CardDescription className="mt-1 flex items-start gap-1.5 text-sm leading-5">
                <Calendar className="mt-0.5 size-4 shrink-0" />
                <span className="min-w-0">{scheduleSummary.text}</span>
              </CardDescription>
            </div>
          </div>
        </ActionRow>

        <div className="flex max-w-full flex-wrap items-center gap-2 @[47rem]/seasonal-update-row:justify-end">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-3 py-2">
            <form.AppField name={`schedules.${scheduleKey}.is_enabled`}>
              {(field) => (
                <Switch
                  checked={Boolean(field.state.value)}
                  disabled={!canToggleSchedule}
                  aria-label={scheduleToggleAriaLabel}
                  onCheckedChange={field.handleChange}
                />
              )}
            </form.AppField>
            <span className="text-sm font-medium text-muted-foreground">{scheduleToggleLabel}</span>
          </div>

          {canReturnToDefault ? (
            <Button type="button" size="sm" onClick={() => onReturnToDefault(design)}>
              Return to everyday design
            </Button>
          ) : null}

          <Button type="button" size="sm" variant="outline" onClick={() => onPreview(design.id)}>
            <Eye className="size-4" />
            Preview
          </Button>

          {!isReadOnly ? (
            <Button size="sm" variant="secondary" asChild>
              <Link {...path.designs.show(design.id).link}>
                <Pencil className="size-4" />
                Edit
              </Link>
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

function SeasonalUpdatesEmptyState() {
  return (
    <EmptyState
      icon={Calendar}
      size="compact"
      surface="transparent"
      title="No seasonal updates yet"
      description="Create an occasion-based update before turning holiday schedules on."
    />
  )
}

function SeasonalUpdatesPanel({
  activeDesignId,
  activeScheduledDesignId,
  defaultDesign,
  form,
  isDefaultActive,
  isPending,
  isReadOnly,
  onReturnToDefault,
  onTurnAll,
  onPreview,
  onSelect,
  orgId,
  selectedDesignId,
  seasonalDesigns,
  useInternalScroll = true,
}) {
  const listContent = (
    <div className="space-y-6 pr-4 pb-4">
      <section aria-labelledby="everyday-design-heading" className="space-y-2.5">
        <div className="flex items-center gap-3 px-0.5">
          <Typography variant="labelStrong" id="everyday-design-heading" className="shrink-0">
            Everyday
          </Typography>
          <Typography variant="caption" className="shrink-0">
            Your storefront foundation
          </Typography>
          <span className="h-px flex-1 bg-border/70" aria-hidden="true" />
        </div>
        <EverydayWebsiteCard
          design={defaultDesign}
          isActive={isDefaultActive}
          isReadOnly={isReadOnly}
          orgId={orgId}
        />
      </section>

      <section aria-labelledby="seasonal-schedule-heading" className="space-y-3">
        <div className="space-y-2 px-0.5">
          <div className="flex items-center gap-3">
            <Typography variant="labelStrong" id="seasonal-schedule-heading" className="shrink-0">
              Seasonal storefront schedule
            </Typography>
            <Typography variant="caption" className="shrink-0">
              {seasonalDesigns.length} {seasonalDesigns.length === 1 ? 'update' : 'updates'}
            </Typography>
            <span className="h-px flex-1 bg-border/70" aria-hidden="true" />
          </div>
          {!isReadOnly && seasonalDesigns.length > 0 ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Button
                type="button"
                size="inline"
                variant="link"
                disabled={isPending}
                onClick={() => onTurnAll(true)}
              >
                Turn all on
              </Button>
              <Button
                type="button"
                size="inline"
                variant="link"
                disabled={isPending}
                onClick={() => onTurnAll(false)}
              >
                Turn all off
              </Button>
            </div>
          ) : null}
        </div>

        <div className="space-y-3">
          {seasonalDesigns.length > 0 ? (
            seasonalDesigns.map((design) => (
              <form.Subscribe
                key={design.id}
                selector={(state) =>
                  state.values.schedules?.[designScheduleFormKey(design.id)]?.is_enabled
                }
              >
                {(scheduleEnabled) => (
                  <SeasonalUpdateRow
                    activeDesignId={activeDesignId}
                    activeScheduledDesignId={activeScheduledDesignId}
                    defaultDesign={defaultDesign}
                    design={design}
                    form={form}
                    isPending={isPending}
                    isReadOnly={isReadOnly}
                    isSelected={Number(selectedDesignId) === Number(design.id)}
                    onPreview={onPreview}
                    onReturnToDefault={onReturnToDefault}
                    onSelect={onSelect}
                    scheduleEnabled={scheduleEnabled}
                    scheduleKey={designScheduleFormKey(design.id)}
                  />
                )}
              </form.Subscribe>
            ))
          ) : (
            <SeasonalUpdatesEmptyState />
          )}
        </div>
      </section>
    </div>
  )

  if (useInternalScroll) {
    return (
      <ScrollArea data-seasonal-designs-scroll-area className="h-full min-h-0">
        {listContent}
      </ScrollArea>
    )
  }

  return listContent
}

function SelectedDesignPreviewPanel({
  canUsePreview = true,
  collections,
  defaultDesign,
  defaultDesignDetail,
  organization,
  products,
  selectedDesignDetail,
  selectedDesignSummary,
  themes,
}) {
  const [previewPage, setPreviewPage] = useState('home')
  const [previewDevice, setPreviewDevice] = useState('desktop')
  const design = selectedDesignSummary
    ? (selectedDesignDetail ?? null)
    : (defaultDesignDetail ?? defaultDesign)

  if (!canUsePreview) {
    return (
      <aside className="h-full min-h-0">
        <Card className="h-full min-h-0">
          <CardContent className="flex h-full min-h-0 items-center justify-center p-6 text-center">
            <div className="max-w-md space-y-3">
              <span className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-interaction">
                <Eye className="size-5" />
              </span>
              <div className="space-y-1">
                <Typography variant="h3">Preview unavailable for this role</Typography>
                <Typography variant="supporting">
                  This role can still review website updates here, but the live storefront preview
                  needs additional website access.
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </aside>
    )
  }

  if (!design) {
    return (
      <Card className="h-full min-h-0">
        <CardContent className="flex h-full min-h-0 items-center justify-center p-6 text-center">
          <div className="space-y-3">
            <span className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-interaction">
              <LoaderCircle className="size-5 animate-spin" />
            </span>
            <div className="space-y-1">
              <Typography variant="h3">Preparing storefront preview</Typography>
              <Typography variant="supporting">
                Loading the selected website update before showing the preview.
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <aside className="h-full min-h-0">
      <Card surface="preview" className="h-full min-h-0 overflow-hidden">
        <CardContent className="h-full min-h-0 p-0">
          <StorefrontPreviewFrame
            design={design}
            defaultDesign={defaultDesignDetail ?? defaultDesign}
            organization={organization}
            themes={themes}
            collections={collections}
            products={products}
            previewPage={previewPage}
            onPreviewPageChange={setPreviewPage}
            previewDevice={previewDevice}
            onPreviewDeviceChange={setPreviewDevice}
            syncDraft={false}
            className="h-full min-h-0 rounded-none border-0 shadow-none"
            viewportClassName="h-full min-h-0"
          />
        </CardContent>
      </Card>
    </aside>
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/designs/')({
  component: DesignIndexPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'designs')) throw notFound()

    if (Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)) {
      throw redirect({
        to: '/$orgId/seasonal-content',
        params: { orgId: params.orgId },
      })
    }

    const loaderPromises = [
      context.queryClient.ensureQueryData(designQueries.index({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(designQueries.default({ organizationId: params.orgId })),
    ]

    if (context.ability.can('list', 'stores') || context.ability.can('view', 'stores')) {
      loaderPromises.push(
        context.queryClient.ensureQueryData(storeQueries.show({ organizationId: params.orgId })),
        context.queryClient.ensureQueryData(storeQueries.themes({ organizationId: params.orgId }))
      )
    }

    if (context.ability.can('list', 'collections') || context.ability.can('view', 'collections')) {
      loaderPromises.push(
        context.queryClient.ensureQueryData(
          collectionQueries.index({
            organizationId: params.orgId,
            filters: designCollectionsFilters,
          })
        )
      )
    }

    if (context.ability.can('list', 'products') || context.ability.can('view', 'products')) {
      loaderPromises.push(
        context.queryClient.ensureQueryData(
          productQueries.index({
            organizationId: params.orgId,
            filters: designProductsFilters,
          })
        )
      )
    }

    await Promise.all(loaderPromises)

    return { isReadOnly: !context.ability.can('edit', 'designs') }
  },
})

export function DesignIndexPage() {
  const ability = useAbility()
  const { isReadOnly = false } = Route.useLoaderData()
  const [selectedDesignId, setSelectedDesignId] = useState(null)
  const [isReadinessNoticeVisible, setIsReadinessNoticeVisible] = useState(true)
  const previewPanelRef = useRef(null)
  const isWideScreen = useMediaQuery('(min-width: 1280px)')
  const { orgId } = useParams({ strict: false })
  const { data: designs } = useDesigns()
  const path = usePathHelper()
  const navigate = useNavigate()
  const canReadCurrentOrganization = Boolean(orgId)
  const canReadStores = ability.can('list', 'stores') || ability.can('view', 'stores')
  const canReadCollections =
    ability.can('list', 'collections') || ability.can('view', 'collections')
  const canReadProducts = ability.can('list', 'products') || ability.can('view', 'products')
  const canUsePreview = canReadCurrentOrganization && canReadStores
  const { data: organization } = useQuery({
    ...organizationQueries.show(orgId),
    enabled: canReadCurrentOrganization,
  })
  const { data: store } = useQuery({
    ...storeQueries.show({ organizationId: orgId }),
    enabled: Boolean(orgId) && canReadStores,
  })
  const { mutateAsync: activateDesign, isPending: isActivatingDesign } = useActivateDesignMutation()
  const updateScheduleStatesMutation = useUpdateDesignScheduleStatesMutation()
  const demoUrl = buildDemoStorefrontUrl(organization?.demo_id, import.meta.env.VITE_DEMO_URL)
  const activeScheduledDesignId = Number(store?.active_scheduled_design_id) || null
  const activeDesignId = activeScheduledDesignId || Number(store?.active_design_id) || null
  const isPending = isActivatingDesign || updateScheduleStatesMutation.isPending

  const sortedDesigns = useMemo(() => {
    return [...(designs ?? [])].sort((a, b) => {
      const rank = (design) => {
        if (design?.is_default) return 0
        if (activeDesignId && Number(design?.id) === activeDesignId) return 1
        return 2
      }

      const rankDiff = rank(a) - rank(b)
      if (rankDiff !== 0) {
        return rankDiff
      }

      return safeDateValue(b?.updated_at) - safeDateValue(a?.updated_at)
    })
  }, [activeDesignId, designs])

  const defaultDesign = sortedDesigns.find((design) => design?.is_default) ?? null
  const seasonalDesigns = useMemo(() => {
    return sortedDesigns
      .filter((design) => !design?.is_default)
      .sort((a, b) => {
        const isActive = (design) => activeDesignId && Number(design?.id) === Number(activeDesignId)

        if (isActive(a) !== isActive(b)) {
          return isActive(a) ? -1 : 1
        }

        const startDiff = designStartsAtValue(a) - designStartsAtValue(b)
        if (startDiff !== 0) {
          return startDiff
        }

        const scheduleRank = (design) => {
          if (design?.schedule?.is_enabled) return 0
          if (design?.schedule) return 1
          return 2
        }
        const scheduleRankDiff = scheduleRank(a) - scheduleRank(b)
        if (scheduleRankDiff !== 0) {
          return scheduleRankDiff
        }

        return String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
      })
  }, [activeDesignId, sortedDesigns])
  const scheduleDefaultValues = useMemo(
    () => buildDesignScheduleFormValues(seasonalDesigns),
    [seasonalDesigns]
  )
  const scheduleForm = useAppForm({
    defaultValues: scheduleDefaultValues,
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        const changedSchedules = buildChangedDesignSchedulePayloads(
          value.schedules,
          scheduleDefaultValues.schedules
        )

        if (!changedSchedules.length) {
          formApi.reset(value)
          return
        }

        try {
          await updateScheduleStatesMutation.mutateAsync(changedSchedules)
          formApi.reset(value)
          toast.success(
            changedSchedules.length === 1 ? 'Seasonal schedule saved' : 'Seasonal schedules saved'
          )
        } catch (error) {
          toast.error('Seasonal schedules were not saved', {
            description: getErrorMessage(error),
          })
        }
      },
    },
  })
  const selectedDesign =
    seasonalDesigns.find((design) => Number(design?.id) === Number(selectedDesignId)) ??
    seasonalDesigns.find(
      (design) => activeDesignId && Number(design?.id) === Number(activeDesignId)
    ) ??
    seasonalDesigns[0] ??
    null
  const { data: selectedDesignDetail = null } = useQuery({
    ...designQueries.show({
      organizationId: orgId,
      designId: selectedDesign?.id ?? 0,
    }),
    enabled: Boolean(selectedDesign?.id),
  })
  const { data: defaultDesignDetail = null } = useQuery({
    ...designQueries.default({ organizationId: orgId }),
    enabled: Boolean(defaultDesign?.id),
  })
  const { data: themes = [] } = useQuery({
    ...storeQueries.themes({ organizationId: orgId }),
    enabled: Boolean(orgId) && canReadStores,
  })
  const { data: collections = [] } = useQuery({
    ...collectionQueries.index({
      organizationId: orgId,
      filters: designCollectionsFilters,
    }),
    enabled: Boolean(orgId) && canReadCollections,
  })
  const { data: products = [] } = useQuery({
    ...productQueries.index({
      organizationId: orgId,
      filters: designProductsFilters,
    }),
    enabled: Boolean(orgId) && canReadProducts,
  })
  const activeSeasonalDesign =
    seasonalDesigns.find(
      (design) => activeDesignId && Number(design?.id) === Number(activeDesignId)
    ) ?? null
  const isDefaultActive = Boolean(defaultDesign && !activeSeasonalDesign)
  const handleCreateDesign = () => navigate({ ...path.designs.create().link })
  const handleSelectDesign = (designId) => setSelectedDesignId(Number(designId))
  const handlePreviewDesign = (designId) => {
    setSelectedDesignId(Number(designId))

    if (isWideScreen || typeof window === 'undefined') {
      return
    }

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        previewPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 0)
    })
  }

  const handleReturnToDefault = async () => {
    if (!defaultDesign?.id) {
      return
    }

    try {
      await activateDesign(defaultDesign.id)
      toast.success('Returned to everyday website')
    } catch (error) {
      toast.error('Unable to return to everyday website', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleTurnAll = (isEnabled) => {
    Object.entries(scheduleForm.state.values.schedules).forEach(([key, schedule]) => {
      if (schedule.has_schedule) {
        scheduleForm.setFieldValue(`schedules.${key}.is_enabled`, isEnabled)
      }
    })
  }

  return (
    <scheduleForm.AppForm>
      <scheduleForm.Form
        className={classNames(
          'w-full',
          isWideScreen
            ? 'flex h-[calc(100dvh-5.5rem)] min-h-0 flex-col gap-3 overflow-hidden'
            : 'space-y-4 pb-6'
        )}
      >
        <AdminPageHeader
          icon={Wallpaper}
          title="Website designs"
          description="Manage your everyday storefront and seasonal update schedule."
          density="compact"
          actions={
            <>
              {demoUrl ? (
                <AdminHeaderActionButton asChild variant="outline">
                  <a href={demoUrl} target="_blank" rel="noopener noreferrer">
                    View storefront
                    <ExternalLink className="size-4" />
                  </a>
                </AdminHeaderActionButton>
              ) : null}
              <Can do="create" on="designs">
                <CreateActionButton onClick={handleCreateDesign}>
                  Create seasonal update
                </CreateActionButton>
              </Can>
            </>
          }
        />

        {isReadinessNoticeVisible ? (
          <scheduleForm.Subscribe selector={(state) => state.values.schedules}>
            {(schedules) => (
              <SeasonalReadinessSummary
                designs={seasonalDesigns}
                schedules={schedules}
                onDismiss={() => setIsReadinessNoticeVisible(false)}
              />
            )}
          </scheduleForm.Subscribe>
        ) : null}

        <div className={classNames(isWideScreen && 'min-h-0 flex-1 overflow-hidden')}>
          <DesignsSplitLayout
            settingsPanel={
              <SeasonalUpdatesPanel
                activeDesignId={activeDesignId}
                activeScheduledDesignId={activeScheduledDesignId}
                defaultDesign={defaultDesign}
                form={scheduleForm}
                isDefaultActive={isDefaultActive}
                isPending={isPending}
                isReadOnly={isReadOnly}
                onPreview={handlePreviewDesign}
                onReturnToDefault={handleReturnToDefault}
                onTurnAll={handleTurnAll}
                onSelect={handleSelectDesign}
                orgId={orgId}
                selectedDesignId={selectedDesign?.id}
                seasonalDesigns={seasonalDesigns}
                useInternalScroll={isWideScreen}
              />
            }
            previewPanel={
              <SelectedDesignPreviewPanel
                canUsePreview={canUsePreview}
                collections={collections}
                defaultDesign={defaultDesign}
                defaultDesignDetail={defaultDesignDetail}
                organization={organization}
                products={products}
                selectedDesignDetail={selectedDesignDetail}
                selectedDesignSummary={selectedDesign}
                themes={themes}
              />
            }
            previewPanelRef={previewPanelRef}
          />
        </div>

        {!isReadOnly ? <SaveBar mutation={updateScheduleStatesMutation} /> : null}
      </scheduleForm.Form>
    </scheduleForm.AppForm>
  )
}
